import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import useSWR from "swr";
import Head from "next/head";
import classNames from "classnames";
import axios from "axios";

import api, { fetchTenantConfig } from "../../services/api";

import Layout from "../../components/Layout";
import PassGrid from "../../components/PassGrid";
import NoContentPlaceholder from "../../components/NoContentPlaceholder";
import CheckoutModal from "../../components/CheckoutModal";
import Modal from "../../components/Modal";
import { useAuth } from "../../hooks/useAuth";
import { capitalize, formatInsertables, formatPrice } from "../../util";
import Button from "../../components/Button";

export default function Passes({ initialData }) {
  const router = useRouter();
  const { tenant, token, user, apiLoaded } = useAuth();

  const [selectedPass, setSelectedPass] = useState(null);
  const [selectedPassBucket, setSelectedPassBucket] = useState(null);
  const [selectedSalesOption, setSelectedSalesOption] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [isPassSaleComplete, setIsPassSaleComplete] = useState(false);
  const [isDataUpdate, setIsDataUpdate] = useState(false);

  const { data: passResponse, mutate, error } = useSWR(
    apiLoaded &&
      user &&
      `people/self/passes_including_global?event_bucket=${tenant.event_bucket}&token=${token}`,
    k => api.get(k).then(r => r.data)
  );
  const {
    data: passBucketResponse,
    error: passBucketError
  } = useSWR(
    apiLoaded &&
      `event_buckets/${tenant.event_bucket}/pass_buckets?include_checkout_options=true`,
    k => api.get(k).then(r => r.data),
    { initialData }
  );

  const [desiredCurrency, setDesiredCurrency] = useState(tenant.currency);
  useEffect(() => {
    if (typeof window !== "undefined") {
      axios
        .post("/api/geo_currency_lookup")
        .then(r => {
          let userCurrency = r.data?.currency?.toLowerCase();
          if (userCurrency) {
            setDesiredCurrency(userCurrency);
          }
        })
        .catch(e => {
          console.log(`Failed to determine user currency`, e);
        });
    }
  }, [typeof window]);

  if (!passBucketResponse && !passBucketError) {
    return <Layout loader />;
  }

  function handleEditPassDetailsClick(pass) {
    setIsDataUpdate(true);
    setSelectedPass(pass);
    setSelectedPassBucket(null);
    setSelectedSalesOption(null);
    setShowCheckoutModal(true);
    return;
  }

  function handlePurchaseClick(passBucket, salesOption) {
    setIsDataUpdate(false);
    setSelectedPass(null);
    setSelectedPassBucket(passBucket);
    setSelectedSalesOption(salesOption);
    setShowCheckoutModal(true);
    return;
  }

  const hasPasses = passResponse?.passes && passResponse?.passes.length && user;

  return (
    <Layout>
      <Head>
        <title>
          {capitalize(
            tenant.virtual_festival_pass_sales_label || tenant.display_passes
          )}{" "}
          | {tenant.display_name}
        </title>
      </Head>
      {showCheckoutModal ? (
        <CheckoutModal
          pass={selectedPassBucket ? selectedPassBucket : selectedPass}
          salesOption={selectedSalesOption}
          isDataUpdate={isDataUpdate}
          onBackgroundDismiss={() => setShowCheckoutModal(false)}
          onSuccess={data => {
            mutate();
            setShowCheckoutModal(false);
            setIsPassSaleComplete(true);
            setSelectedSalesOption(null);
            setSelectedPass(null);
            setSelectedPassBucket(null);
          }}
        />
      ) : null}
      {isPassSaleComplete ? (
        <Modal
          onBackgroundDismiss={() => setIsPassSaleComplete(false)}
          actions={[
            {
              title: "Close",
              primary: true,
              className: "",
              onClick: function () {
                setIsPassSaleComplete(false);
                setIsDataUpdate(false);
              }.bind(this)
            }
          ]}
          body={
            isDataUpdate ? (
              <div>
                You have successfully updated your {tenant.display_pass}{" "}
                details.
              </div>
            ) : (
              <div>
                Your purchase is complete. Your {tenant.display_pass} will
                appear in the{" "}
                <strong>My {capitalize(tenant.display_passes)}</strong> section
                of this page. Use your {tenant.display_pass} to unlock content
                by selecting your {tenant.display_pass} in the dropdown menu
                when unlocking a new program.
              </div>
            )
          }
        ></Modal>
      ) : null}
      <div>
        {hasPasses ? (
          <div className="pb-8">
            <h2 className="text-2xl leading-9 font-bold text-white justify-center pb-5">
              My {tenant.display_passes}
            </h2>
            <PassGrid
              items={passResponse.passes.map(pass => ({
                ...pass,
                virtual_content_type: "livestream"
              }))}
              festivalId={router.query.festivalId}
              onItemClick={handleEditPassDetailsClick}
            />
          </div>
        ) : null}
        {tenant.virtual_pass_sales_sections?.map(section => {
          return (
            <div className="pb-8">
              <h2 className="text-2xl leading-9 font-bold text-white justify-center pb-5">
                {section.title}
              </h2>
              {section.introduction?.__html ? (
                <div className="text-gray-100 text-center bg-gray-700 p-4 rounded-lg mb-8">
                  <div
                    className="tenant-injected-html"
                    dangerouslySetInnerHTML={{
                      __html: section.introduction?.__html
                    }}
                  />
                </div>
              ) : null}
              {section.passes?.length ? (
                <div className="mb-8 flex flex-wrap gap-8 justify-center">
                  {section.passes.map(code => {
                    const passBucket = passBucketResponse?.pass_buckets?.find(
                      b => b.code === code
                    );

                    const detail = tenant.virtual_pass_sales_details?.[code];

                    const availableCurrencyPrices = [
                      { currency: tenant.currency, price: passBucket?.price }
                    ].concat(
                      passBucket?.multicurrency_price_enabled
                        ? passBucket.multicurrency_price
                        : []
                    );
                    const selectedPrice =
                      availableCurrencyPrices.find(
                        p => p.currency === desiredCurrency
                      ) || availableCurrencyPrices[0];

                    return passBucket ? (
                      <div
                        className="flex-1 text-white px-6 py-4 border border-gray-600 bg-gray-700 rounded-md flex flex-col items-center"
                        style={{ maxWidth: 400, flexBasis: 400 }}
                      >
                        <h3 className="font-bold text-center text-xl mb-2">
                          {detail?.title || passBucket?.name}
                        </h3>
                        <div
                          className="tenant-injected-html mb-4"
                          dangerouslySetInnerHTML={{
                            __html: detail?.description?.__html
                          }}
                        />
                        <Button
                          onClick={() => {
                            handlePurchaseClick(passBucket, selectedPrice);
                          }}
                        >
                          Buy now (
                          {formatPrice(selectedPrice.price, {
                            short: true,
                            currency: selectedPrice.currency
                          })}
                          )
                        </Button>
                        {availableCurrencyPrices.length > 1 ? (
                          <div className="mt-4 text-sm">
                            <span className="mr-1">Available in:</span>
                            {availableCurrencyPrices.map(p => (
                              <button
                                key={p.currency}
                                onClick={() => setDesiredCurrency(p.currency)}
                                className={classNames(
                                  "mr-0.5 px-1 py-0.5 rounded focus:outline-none",
                                  p.currency === desiredCurrency
                                    ? "bg-gray-500 text-gray-50"
                                    : "hover:underline"
                                )}
                              >
                                {p.currency.toUpperCase()}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null;
                  })}
                </div>
              ) : null}
              {section.notice?.__html ? (
                <div className="text-gray-100 text-center bg-gray-700 p-4 rounded-lg mb-8">
                  <div
                    className="tenant-injected-html"
                    dangerouslySetInnerHTML={{
                      __html: section.notice?.__html
                    }}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Layout>
  );
}

export async function getServerSideProps({ query, res, locale }) {
  if (res) {
    res.setHeader("Cache-Control", "s-maxage=1, stale-while-revalidate");
  }

  const tenant = await fetchTenantConfig(query.festivalId);

  let finalProps;
  try {
    const data = await api
      .get(
        `event_buckets/${tenant.event_bucket}/pass_buckets?include_checkout_options=true`,
        {
          auth: { username: tenant.api_key }
        }
      )
      .then(r => r.data);
    finalProps = { initialData: data, initialTenant: tenant };
  } catch (e) {
    finalProps = {};
  }
  return {
    props: {
      ...finalProps
    }
  };
}
